import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { priceLabel } from "@/lib/cart";
import { ShieldCheck, FlaskConical, MapPin, Leaf, FileCheck, ArrowRight, Phone, Mail, FileText, BadgeCheck, Clock, Moon, Sparkles, Brain, Zap, Heart, Truck, Award, Star, ChevronDown, Trophy, Compass, Store, Candy, CupSoda, Sprout } from "lucide-react";
import HelpMeChoose from "@/components/HelpMeChoose";
import EmailCapture from "@/components/EmailCapture";
import PromoSection from "@/components/PromoSection";
import FeaturedCarousel from "@/components/FeaturedCarousel";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/05ed59e7-260e-4506-82d1-1ad035a972d2/images/2b36576f1cd443faf824f86328400054bdef4299fee55a7b53b0d11fd88cf307.png";
const TEXTURE_IMG = "https://static.prod-images.emergentagent.com/jobs/05ed59e7-260e-4506-82d1-1ad035a972d2/images/cdcc90a4085c73381d8d775ab57ed4a3d019b47c12983f1e43c29c3833d6893e.png";
const LIFESTYLE_IMG = "https://images.pexels.com/photos/8939960/pexels-photo-8939960.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const trustItems = [
  { icon: FlaskConical, title: "Third-Party Lab Tested", desc: "Every batch verified by independent labs." },
  { icon: FileCheck, title: "Farm Bill Compliant", desc: "Meets 2018 Farm Bill standards." },
  { icon: ShieldCheck, title: "Age-Restricted (21+)", desc: "Adults only. ID verification at every step." },
  { icon: MapPin, title: "Texas Focused", desc: "Built for the Lone Star State, locally operated." },
  { icon: Leaf, title: "Transparent Sourcing", desc: "Hemp from licensed U.S. farms — fully traceable." },
];

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [products, setProducts] = useState([]);
  const [newDrops, setNewDrops] = useState([]);
  const [brands, setBrands] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/products", { params: { is_new: true } }).then((r) => setNewDrops(r.data));
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  // Pick one top product per category for "Best by category"
  const bestByCategory = categories.map((c) => {
    const candidates = products.filter((p) => p.category_id === c.id);
    return { category: c, product: candidates[0] };
  }).filter((x) => x.product);

  // Which category slugs have at least one new drop?
  const newCategorySlugs = new Set(
    newDrops.map((p) => {
      const c = categories.find((cat) => cat.id === p.category_id);
      return c?.slug;
    }).filter(Boolean)
  );

  return (
    <div data-testid="home-page" className="bg-black">
      {/* 1 — Hero: Texas emblem + address on a solid black background */}
      <section data-testid="home-hero" className="bg-black">
        <div className="max-w-4xl mx-auto px-6 pt-8 lg:pt-12 pb-6 text-center">
          <img
            src="/texas-logo.png"
            alt="High Achievers Texas"
            data-testid="home-texas-logo"
            className="mx-auto w-full max-w-[260px] sm:max-w-[320px] h-auto"
            draggable={false}
          />
          <div className="mt-1" data-testid="home-address">
            <div className="font-serif text-xl sm:text-2xl text-sand leading-tight">{settings?.address_line1 || "3100 Summerhill Rd"}</div>
            <div className="text-sm text-sand/60 mt-1">{settings?.address_line2 || "Texarkana, TX 75503"}</div>
          </div>
        </div>

        {/* Free-shipping line — sits right above the carousel */}
        <div data-testid="home-free-shipping" className="bg-navy text-tan text-center py-3.5 px-4 text-base sm:text-xl uppercase tracking-[0.22em] font-semibold">
          All Orders Over $100 — Free Shipping!
        </div>

        {/* Featured products carousel */}
        <div className="bg-black py-10 lg:py-14">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <h2 className="font-serif text-3xl sm:text-4xl text-sand mb-8">Featured Products</h2>
            <FeaturedCarousel products={featured.length ? featured : products} />
            <div className="mt-8 text-center">
              <Link to="/shop" data-testid="featured-shop-now" className="inline-flex items-center gap-2 bg-copper text-navy px-10 py-4 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C158] transition-colors">
                Shop Now <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>

        {/* Gummies banner + Shop Gummies */}
        <Link to="/shop?category=edibles&q=House%20Gummies" data-testid="home-gummies-banner" className="block">
          <img
            src="/gummies-banner-v3.png"
            alt="Summer Time Vibes — THC gummies enhanced with CBG for focus, 25mg potency, and CBN for calm & sleep"
            className="w-full h-auto"
            draggable={false}
          />
        </Link>
        <div className="bg-black py-8 text-center">
          <Link
            to="/shop?category=edibles&q=House%20Gummies"
            data-testid="home-gummies-shop-now"
            className="inline-flex items-center gap-2 bg-copper text-navy px-10 py-4 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C158] transition-colors"
          >
            Shop Gummies <ArrowRight size={16} />
          </Link>
        </div>

        {/* Explore by category — breaks up the banners with quick shop navigation */}
        <div data-testid="home-category-trio" className="bg-black py-12 lg:py-16 border-t border-copper/15">
          <div className="max-w-6xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-8">
              <span className="eyebrow">Explore the Shop</span>
              <h2 className="font-serif text-3xl sm:text-4xl text-sand mt-2 leading-tight">What are you in the mood for?</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: Candy, label: "Edibles", to: "/shop?category=edibles" },
                { icon: CupSoda, label: "Drinks", to: "/shop?category=drinks" },
                { icon: Sprout, label: "Mushrooms", to: "/shop?category=mushrooms" },
              ].map((c) => (
                <Link
                  key={c.label}
                  to={c.to}
                  data-testid={`home-cat-${c.label.toLowerCase()}`}
                  className="group border border-copper/30 hover:border-copper bg-charcoal transition-all p-8 flex flex-col items-center text-center"
                >
                  <div className="w-14 h-14 rounded-full bg-black flex items-center justify-center group-hover:bg-copper/15 transition-colors">
                    <c.icon size={26} strokeWidth={1.5} className="text-copper" />
                  </div>
                  <div className="mt-4 font-serif text-2xl text-sand">{c.label}</div>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-sand/50 group-hover:text-copper">Shop {c.label} →</div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Drinks banner + Shop Drinks */}
        <Link to="/shop?category=drinks" data-testid="home-drinks-banner" className="block">
          <img
            src="/drinks-banner.jpg"
            alt="High Achievers hemp-infused drinks — Enjoy, Uncle Skunks, Moonwater"
            className="w-full h-auto"
            draggable={false}
          />
        </Link>
        <div className="bg-black py-8 text-center">
          <Link
            to="/shop?category=drinks"
            data-testid="home-drinks-shop-now"
            className="inline-flex items-center gap-2 bg-copper text-navy px-10 py-4 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C158] transition-colors"
          >
            Shop Drinks <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Email capture — 20% off */}
      <EmailCapture />

      {/* Wyld — logo + its products + Shop Now, directly under the email signup */}
      <BrandSection matchKey="wyld" brands={brands} products={products} />

      {/* 3 — Shop by Benefit (moved up — first scroll-revealed section, right under the logo/hero) */}
      <section className="bg-sand py-20 lg:py-24 border-t border-tan/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="eyebrow">Shop by How You Want to Feel</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4 leading-tight">What are you looking to support today?</h2>
            <div className="divider my-6" />
            <p className="text-lg text-charcoal/85 leading-relaxed font-light">
              Tell us what you&apos;re after — sleep, calm, focus, energy, or recovery — and we&apos;ll point you to the products our customers reach for most often.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-12">
            {[
              { icon: Moon, label: "Better Sleep", to: "/shop?category=edibles", testid: "benefit-sleep" },
              { icon: Sparkles, label: "Unwind & Relax", to: "/shop?category=drinks", testid: "benefit-relax" },
              { icon: Brain, label: "Calm Focus", to: "/shop?category=mushrooms", testid: "benefit-focus" },
              { icon: Zap, label: "Daytime Lift", to: "/shop?category=drinks", testid: "benefit-energy" },
              { icon: Heart, label: "Recovery", to: "/shop?category=mushrooms", testid: "benefit-recover" },
            ].map((b) => (
              <Link
                key={b.label}
                to={b.to}
                data-testid={b.testid}
                className="group float-card bg-sand border border-tan/40 hover:border-navy transition-all px-5 py-7 flex flex-col items-center text-center"
              >
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center group-hover:bg-navy/10 transition-colors">
                  <b.icon size={22} strokeWidth={1.5} className="text-navy" />
                </div>
                <div className="mt-4 font-serif text-lg text-navy">{b.label}</div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-charcoal/60 group-hover:text-copper">Explore →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Jolly — logo + its products + Shop Now, under the "what are you looking to support" block */}
      <BrandSection matchKey="jolly" brands={brands} products={products} />

      {/* Current Promotions (employee-managed) */}
      <PromoSection />

      {/* 4 — Product Categories */}
      <section className="bg-navy py-24 lg:py-32 border-t border-copper/15">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <span className="eyebrow">Shop by Category</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-sand mt-4 leading-tight">Explore our curated catalog.</h2>
            <div className="divider my-6" />
            <p className="text-lg text-sand/80 leading-relaxed">
              Each category is built around a clear use-case, with every product third-party tested and clearly labeled.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/shop?category=${c.slug}`}
                data-testid={`category-card-${c.slug}`}
                className="group float-card bg-sand border border-tan/40 overflow-hidden hover:border-navy transition-all"
              >
                <div className="aspect-[4/3] overflow-hidden bg-sand">
                  {c.image_url && <img src={mediaUrl(c.image_url)} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                </div>
                <div className="p-7">
                  <div className="font-serif text-2xl text-navy">{c.name}</div>
                  <p className="text-sm text-charcoal/75 mt-2 leading-relaxed line-clamp-2">{c.description}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-copper group-hover:text-texasred">
                    Browse <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/shop" className="text-sm uppercase tracking-[0.2em] text-sand border-b border-copper hover:text-copper pb-1">
              View all products
            </Link>
          </div>
        </div>
      </section>

      {/* 4 — Trust & Compliance */}
      <section className="bg-sand/85 backdrop-blur-sm py-24 lg:py-32 border-t border-white/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="eyebrow">Why We&apos;re Different</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4 leading-tight">A standard you can verify.</h2>
            <div className="divider my-6" />
            <p className="text-lg text-charcoal/85 leading-relaxed">
              We treat compliance and transparency as the product — not an afterthought.
              Every item we sell is built around the same five principles.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mt-14">
            {trustItems.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex flex-col gap-3" data-testid={`trust-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                <Icon size={30} strokeWidth={1.25} className="text-copper" />
                <div className="font-serif text-xl text-navy mt-2">{title}</div>
                <p className="text-sm text-charcoal/80 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — COA / Lab Testing */}
      <section className="bg-navy text-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <span className="eyebrow text-tan">Lab Transparency</span>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">Every batch. Every cannabinoid. On the record.</h2>
            <div className="divider my-6 bg-tan" />
            <p className="text-lg text-white/80 leading-relaxed max-w-xl font-light">
              Independent labs test every batch we sell for potency and contaminants — pesticides,
              heavy metals, residual solvents, and microbial activity. The certificates are public.
            </p>
            <ul className="mt-8 space-y-4 text-white/85">
              {[
                "Third-party ISO-accredited laboratories",
                "Cannabinoid potency verified per batch",
                "Pesticide, heavy metal, and microbial screens",
                "Batch number printed on every label",
              ].map((t) => (
                <li key={t} className="flex gap-3"><BadgeCheck size={20} className="text-tan flex-shrink-0 mt-0.5"/><span>{t}</span></li>
              ))}
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/lab-reports" data-testid="home-coa-cta" className="bg-tan text-navy px-10 py-4 text-sm tracking-[0.2em] uppercase hover:bg-white transition-colors text-center inline-flex items-center justify-center gap-2">
                <FileText size={16}/> View Lab Reports
              </Link>
              <Link to="/learn" className="border border-tan text-tan px-10 py-4 text-sm tracking-[0.2em] uppercase hover:bg-tan hover:text-navy transition-colors text-center">
                How testing works
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 border border-white/10 p-6">
                <div className="eyebrow text-tan">Per Serving</div>
                <div className="font-serif text-5xl mt-3">25mg</div>
                <div className="text-sm text-white/60 mt-2">CBD — measured dosing on every edible &amp; drink</div>
              </div>
              <div className="bg-white/5 border border-white/10 p-6">
                <div className="eyebrow text-tan">Delta-9</div>
                <div className="font-serif text-5xl mt-3">&lt;0.3%</div>
                <div className="text-sm text-white/60 mt-2">Farm Bill compliant across the catalog</div>
              </div>
              <div className="bg-white/5 border border-white/10 p-6">
                <div className="eyebrow text-tan">Contaminants</div>
                <div className="font-serif text-5xl mt-3">Pass</div>
                <div className="text-sm text-white/60 mt-2">Pesticides, heavy metals, microbial — all panels</div>
              </div>
              <div className="bg-white/5 border border-white/10 p-6">
                <div className="eyebrow text-tan">Batches Tested</div>
                <div className="font-serif text-5xl mt-3">100%</div>
                <div className="text-sm text-white/60 mt-2">Every batch, every product, before it ships</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7 — Best by Category */}
      {bestByCategory.length > 0 && (
        <section className="bg-sand/85 backdrop-blur-sm py-24 lg:py-32 border-t border-white/20">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
              <div className="max-w-xl">
                <span className="eyebrow">Best Sellers</span>
                <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4 leading-tight">A favorite from every category.</h2>
              </div>
              <Link to="/shop" className="text-sm uppercase tracking-[0.2em] text-navy border-b border-copper hover:border-texasred pb-1">
                See the full catalog
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {bestByCategory.map(({ category, product }) => (
                <div key={category.id} className="flex flex-col">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-copper">{category.name}</div>
                  <Link to={`/shop/${product.slug}`} data-testid={`best-${category.slug}`} className="group float-card bg-sand border border-tan/40 overflow-hidden hover:border-navy transition-all mt-3">
                    <div className="relative aspect-[4/5] overflow-hidden bg-white">
                      {product.image_url && <img src={mediaUrl(product.image_url)} alt={product.name} className="w-full h-full object-contain p-3 group-hover:scale-[1.03] transition-transform duration-700" />}
                      {product.is_new && (
                        <span className="absolute top-3 left-3 bg-copper text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em]">New</span>
                      )}
                    </div>
                  <div className="pt-5 px-5 pb-5">
                    <div className="font-serif text-xl text-navy leading-tight">{product.name}</div>
                    <p className="text-sm text-charcoal/70 mt-2 leading-relaxed line-clamp-2">{product.short_description}</p>
                    <div className="mt-4 flex items-center justify-between border-t border-tan/30 pt-3">
                      <span className="text-sm text-copper">{priceLabel(product)}</span>
                      <span className="text-xs uppercase tracking-[0.2em] text-charcoal/60 group-hover:text-navy">View →</span>
                    </div>
                  </div>
                  </Link>
                  <Link to={`/shop?category=${category.slug}`} className="mt-4 text-[11px] uppercase tracking-[0.25em] text-charcoal/70 hover:text-navy">
                    See all {category.name.toLowerCase()} →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 7d — New Drops */}
      {newDrops.length > 0 && (
        <section data-testid="new-drops" className="relative bg-navy text-white py-24 lg:py-28 overflow-hidden">
          {/* Soft copper glow accent */}
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-copper/10 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 bg-copper text-navy px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.25em]">
                    <Sparkles size={11} strokeWidth={2.5}/> New
                  </span>
                  <span className="eyebrow text-tan">Just Landed</span>
                </div>
                <h2 className="font-serif text-4xl sm:text-5xl mt-5 leading-tight">
                  New drops <span className="italic text-tan">on the shelf.</span>
                </h2>
                <p className="mt-5 text-white/80 text-lg font-light leading-relaxed">
                  Our latest arrivals — small-batch, lab-tested, and ready for adults across Texas.
                </p>
              </div>
              <Link to="/shop" className="text-sm uppercase tracking-[0.2em] text-tan border-b border-tan/60 hover:border-white pb-1">
                Browse the catalog
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
              {newDrops.slice(0, 3).map((p) => (
                <Link key={p.id} to={`/shop/${p.slug}`} data-testid={`new-drop-${p.slug}`} className="group">
                  <div className="relative aspect-[4/5] overflow-hidden bg-white">
                    {p.image_url && (
                      <img src={mediaUrl(p.image_url)} alt={p.name} className="w-full h-full object-contain p-3 group-hover:scale-[1.03] transition-transform duration-700" />
                    )}
                    <span className="absolute top-4 left-4 bg-copper text-navy px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em]">
                      New
                    </span>
                  </div>
                  <div className="pt-5">
                    <div className="font-serif text-2xl text-white leading-tight">{p.name}</div>
                    <p className="text-sm text-white/65 mt-2 leading-relaxed line-clamp-2">{p.short_description}</p>
                    <div className="mt-4 flex items-center justify-between border-t border-white/15 pt-3">
                      <span className="text-sm text-tan">{priceLabel(p)}</span>
                      <span className="text-xs uppercase tracking-[0.2em] text-white/60 group-hover:text-tan">View Details →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 7b — Value Props ribbon */}
      <section data-testid="value-props" className="border-y border-tan/30 py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-5 gap-8 text-center">
          {[
            { icon: Award, label: "Lab Tested\nEvery Batch" },
            { icon: Leaf, label: "Texas-Grown\nHemp" },
            { icon: ShieldCheck, label: "Farm Bill\nCompliant" },
            { icon: Truck, label: "Free Shipping\nOver $100" },
            { icon: Heart, label: "Family\nOwned" },
          ].map((v) => (
            <div key={v.label} className="flex flex-col items-center gap-3" data-testid={`valueprop-${v.label.split('\n')[0].toLowerCase().replace(/\s/g,'-')}`}>
              <v.icon size={28} strokeWidth={1.25} className="text-copper" />
              <div className="text-xs uppercase tracking-[0.18em] text-sand/85 whitespace-pre-line leading-relaxed">{v.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 8 — About High Achievers Texas (with Education above) */}
      {/* 6 — Educational Resources (moved here per design) */}
      <section className="relative py-24 lg:py-32 bg-[#FAFAFA]">
        <div
          className="absolute inset-0 opacity-15 bg-cover bg-center pointer-events-none"
          style={{ backgroundImage: `url(${TEXTURE_IMG})` }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={LIFESTYLE_IMG} alt="Mature couple living a healthy lifestyle" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <span className="eyebrow">Hemp Education</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4 leading-tight">Understanding hemp products.</h2>
            <div className="divider my-6" />
            <p className="text-lg text-charcoal/85 leading-relaxed max-w-2xl">
              An informed customer is a confident customer. Plain-spoken explanations of what these
              compounds are and what&apos;s in your products — without medical claims.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { title: "CBD", body: "A naturally occurring, non-intoxicating compound in the hemp plant." },
                { title: "Functional Mushrooms", body: "Adaptogenic extracts like lion's mane and reishi, blended into select edibles and drinks." },
                { title: "Delta-9", body: "Permitted under the 2018 Farm Bill when below 0.3% by dry weight." },
              ].map((c) => (
                <div key={c.title} className="bg-white p-6 border border-tan/40">
                  <div className="font-serif text-2xl text-navy">{c.title}</div>
                  <p className="mt-2 text-sm text-charcoal/85 leading-relaxed">{c.body}</p>
                </div>
              ))}
            </div>
            <Link to="/learn" data-testid="home-learn-cta" className="inline-flex items-center gap-2 mt-10 text-sm uppercase tracking-[0.2em] text-navy border-b border-copper hover:border-texasred pb-1">
              Read the full education guide <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-sand py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <span className="eyebrow">About High Achievers Texas</span>
          <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4 leading-tight">A premium Texas retailer that happens to sell hemp.</h2>
          <div className="divider mx-auto my-8" />
          <p className="text-lg text-charcoal/90 leading-relaxed max-w-3xl mx-auto font-light">
            {settings?.about_text ||
              "High Achievers Texas was built to give adults across the Lone Star State a calm, trustworthy place to learn about and access premium hemp-derived products."}
          </p>
          <div className="mt-10">
            <Link to="/about" className="text-sm uppercase tracking-[0.2em] text-navy border-b border-copper hover:border-texasred pb-1">
              Read our story
            </Link>
          </div>
        </div>
      </section>

      {/* 8c — Texas Hemp Advocacy / Mission */}
      <section data-testid="advocacy-block" className="bg-navy text-white py-24 lg:py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <span className="eyebrow text-tan">Our Stance</span>
          <h2 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">Protecting adult access to <span className="italic text-tan">Texas hemp.</span></h2>
          <div className="divider mx-auto my-8 bg-tan" />
          <p className="text-lg text-white/85 leading-relaxed max-w-3xl mx-auto font-light">
            Texas hemp regulations evolve. We stay engaged — tracking guidance, supporting responsible
            industry advocacy, and stocking only what&apos;s currently compliant. When the rules change, we adapt
            quickly so our customers are never caught off guard.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/learn" className="border border-tan text-tan px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-tan hover:text-navy transition-colors">
              Learn about Texas Hemp Law
            </Link>
            <Link to="/lab-reports" className="bg-tan text-navy px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-white transition-colors">
              View Compliance & Lab Reports
            </Link>
          </div>
        </div>
      </section>

      {/* 9 — Contact Information */}
      <section className="bg-sand/85 backdrop-blur-sm py-24 lg:py-28 border-t border-white/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <span className="eyebrow">Visit · Call · Write</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4 leading-tight">We&apos;re here to help.</h2>
            <div className="divider my-6" />
            <p className="text-lg text-charcoal/85 leading-relaxed">
              Questions about a product, a batch, or your order? A real person will answer.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            <div className="bg-[#FAFAFA] border border-tan/40 p-8">
              <MapPin size={26} strokeWidth={1.25} className="text-copper" />
              <div className="font-serif text-2xl text-navy mt-4">Visit</div>
              <p className="text-sm text-charcoal/85 mt-3 leading-relaxed">
                {settings?.address_line1}<br/>{settings?.address_line2}
              </p>
              {settings?.address_line1 && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent([settings?.address_line1, settings?.address_line2].filter(Boolean).join(", "))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-directions-link"
                  className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-copper hover:text-texasred border-b border-copper pb-0.5"
                >
                  Get Directions <ArrowRight size={13} />
                </a>
              )}
              {settings?.hours?.length > 0 && (
                <div className="mt-5 text-xs text-charcoal/70 flex items-start gap-2">
                  <Clock size={14} className="text-copper mt-0.5" />
                  <div>
                    {settings.hours.map((h, i) => (
                      <div key={i}>{h.day}: {h.open}–{h.close}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="bg-[#FAFAFA] border border-tan/40 p-8">
              <Phone size={26} strokeWidth={1.25} className="text-copper" />
              <div className="font-serif text-2xl text-navy mt-4">Call</div>
              <p className="text-sm text-charcoal/85 mt-3 leading-relaxed">
                Speak directly with our team during business hours.
              </p>
              <a href={`tel:${settings?.phone}`} className="mt-5 inline-block font-serif text-xl text-navy hover:text-copper">{settings?.phone}</a>
            </div>
            <div className="bg-[#FAFAFA] border border-tan/40 p-8">
              <Mail size={26} strokeWidth={1.25} className="text-copper" />
              <div className="font-serif text-2xl text-navy mt-4">Write</div>
              <p className="text-sm text-charcoal/85 mt-3 leading-relaxed">
                We respond to most inquiries within one business day.
              </p>
              <a href={`mailto:${settings?.email}`} className="mt-5 inline-block font-serif text-xl text-navy hover:text-copper break-all">{settings?.email}</a>
            </div>
          </div>
          <div className="mt-12">
            <Link to="/contact" data-testid="home-contact-cta" className="bg-navy text-white px-10 py-4 text-sm tracking-[0.2em] uppercase hover:bg-charcoal transition-colors inline-block">
              Send a message
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom — quiz CTA + Help Me Choose quiz */}
      <section data-testid="quiz-cta" className="bg-black border-t border-copper/25 py-16 lg:py-20 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <span className="eyebrow">Find Your Fit</span>
          <h2 className="font-serif text-4xl sm:text-5xl text-sand mt-3 leading-tight">Don&apos;t know where to start?</h2>
          <p className="mt-4 text-base text-sand/75 leading-relaxed font-light">
            Take a short quiz and we&apos;ll help you find the perfect products for you.
          </p>
          <button
            type="button"
            data-testid="quiz-cta-btn"
            onClick={() => document.getElementById("quiz")?.scrollIntoView({ behavior: "smooth" })}
            className="mt-8 inline-flex items-center gap-2 bg-copper text-navy px-10 py-4 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C158] transition-colors"
          >
            Take the Quiz <ArrowRight size={16} />
          </button>
        </div>
      </section>
      <div id="quiz" className="scroll-mt-20">
        <HelpMeChoose />
      </div>
    </div>
  );
}

const BRAND_FALLBACK_LOGOS = { wyld: "/wyld-logo.png", jolly: "/jolly-logo.png" };
const BRAND_BG = { wyld: "/wyld-bg.jpg", jolly: "/jolly-bg.jpg" };
// Owner-provided full showcase images (products already fanned around the brand logo).
const BRAND_SHOWCASE = { wyld: "/wyld-showcase.jpg", jolly: "/jolly-showcase.jpg" };

function BrandNameplate({ brand, matchKey }) {
  const logo = brand.logo_url ? mediaUrl(brand.logo_url) : BRAND_FALLBACK_LOGOS[matchKey];
  if (logo) {
    return (
      <img
        src={logo}
        alt={brand.name}
        data-testid={`brand-logo-${matchKey}`}
        className="mx-auto max-h-24 sm:max-h-28 w-auto object-contain [filter:brightness(0)_invert(1)]"
        draggable={false}
      />
    );
  }
  return (
    <div data-testid={`brand-nameplate-${matchKey}`} className="inline-block">
      <span className="font-serif text-4xl sm:text-5xl text-navy tracking-tight">{brand.name}</span>
    </div>
  );
}

function BrandProductCard({ product }) {
  return (
    <Link
      to={`/shop/${product.slug}`}
      data-testid={`brand-product-${product.slug}`}
      className="group float-card bg-white border border-tan/40 overflow-hidden hover:border-navy transition-all flex flex-col"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-white">
        {product.image_url && (
          <img src={mediaUrl(product.image_url)} alt={product.name} className="w-full h-full object-contain p-3 group-hover:scale-[1.03] transition-transform duration-700" />
        )}
        {product.is_new && (
          <span className="absolute top-3 left-3 bg-copper text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em]">New</span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="font-serif text-xl text-navy leading-tight">{product.name}</div>
        <p className="text-sm text-charcoal/70 mt-2 leading-relaxed line-clamp-2">{product.short_description}</p>
        <div className="mt-4 flex items-center justify-between border-t border-tan/30 pt-3">
          <span className="text-sm text-copper">{priceLabel(product)}</span>
          <span className="text-xs uppercase tracking-[0.2em] text-charcoal/60 group-hover:text-navy">View →</span>
        </div>
      </div>
    </Link>
  );
}

function BrandSection({ matchKey, brands, products }) {
  // Resilient match: exact slug, slug prefix, or brand name containing the key
  // (case-insensitive). Covers "Wyld", "WYLD", "Wyld Beverages", trailing spaces, etc.
  const key = (matchKey || "").toLowerCase().trim();
  const brand = (brands || []).find((b) => {
    if (b.active === false) return false;
    const slug = (b.slug || "").toLowerCase();
    const name = (b.name || "").toLowerCase();
    return slug === key || slug.startsWith(key) || name.includes(key);
  });
  const items = brand ? (products || []).filter((p) => p.brand_id === brand.id) : [];
  // Always render the Wyld / Jolly showcase so it never silently disappears — even if the
  // owner hasn't created the brand or tagged products yet, the logo + Shop button still show.
  const displayName = brand?.name || (key ? key.charAt(0).toUpperCase() + key.slice(1) : "Brand");
  const shopHref = brand ? `/shop?brand=${brand.slug}` : "/shop";

  const showcase = BRAND_SHOWCASE[key];
  // Preferred layout: display the owner's full showcase image (logo + products baked in),
  // uncropped, with the Shop button overlaid at the bottom.
  if (showcase) {
    return (
      <section
        data-testid={`brand-section-${key}`}
        className="relative border-t border-copper/20 bg-black"
      >
        <div className="max-w-7xl mx-auto">
          <img
            src={showcase}
            alt={`${displayName} collection`}
            data-testid={`brand-showcase-${key}`}
            className="w-full h-auto block select-none"
            draggable={false}
          />
          <div className="py-8 sm:py-10 flex justify-center">
            <Link
              to={shopHref}
              data-testid={`brand-shop-${key}`}
              className="inline-flex items-center gap-2 bg-copper text-navy px-8 sm:px-10 py-4 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C158] transition-colors"
            >
              Shop {displayName} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const bg = BRAND_BG[key];
  return (
    <section
      data-testid={`brand-section-${key}`}
      className="relative py-16 lg:py-20 border-t border-copper/20 bg-black overflow-hidden"
      style={bg ? { backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
    >
      {bg && <div className="absolute inset-0 bg-black/60 pointer-events-none" />}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        {items.length > 0 ? (
          <BrandConstellation
            matchKey={key}
            brand={brand}
            displayName={displayName}
            items={items}
          />
        ) : (
          <div className="text-center mb-10 lg:mb-14">
            <BrandNameplate brand={brand || { name: displayName }} matchKey={key} />
            {brand?.tagline && (
              <p className="mt-4 text-sand/70 font-light text-base sm:text-lg">{brand.tagline}</p>
            )}
            <div className="divider mx-auto mt-6" />
            <p className="mt-6 text-center text-sand/60 font-light text-base">
              Explore our {displayName} collection.
            </p>
          </div>
        )}
        <div className="mt-12 text-center">
          <Link
            to={shopHref}
            data-testid={`brand-shop-${key}`}
            className="inline-flex items-center gap-2 bg-copper text-navy px-8 sm:px-10 py-4 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C158] transition-colors"
          >
            Shop {displayName} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

// Centered logo with products fanning out around it (left + right on desktop, stacked on mobile).
function BrandConstellation({ matchKey, brand, displayName, items }) {
  const mid = Math.ceil(items.length / 2);
  const left = items.slice(0, mid);
  const right = items.slice(mid);
  return (
    <div data-testid={`brand-constellation-${matchKey}`}>
      {/* Desktop / tablet: products flank a centered logo */}
      <div className="hidden md:grid grid-cols-[1fr_auto_1fr] items-center gap-6 lg:gap-10">
        <div className="flex flex-col gap-6 lg:gap-8">
          {left.map((p) => (
            <BrandOrbitCard key={p.id} product={p} align="right" />
          ))}
        </div>
        <div className="flex flex-col items-center text-center px-2 lg:px-4">
          <BrandNameplate brand={brand || { name: displayName }} matchKey={matchKey} />
          {brand?.tagline && (
            <p className="mt-4 text-sand/70 font-light text-base lg:text-lg max-w-xs">{brand.tagline}</p>
          )}
          <div className="divider mx-auto mt-6" />
        </div>
        <div className="flex flex-col gap-6 lg:gap-8">
          {right.map((p) => (
            <BrandOrbitCard key={p.id} product={p} align="left" />
          ))}
        </div>
      </div>

      {/* Mobile: centered logo on top, product grid below */}
      <div className="md:hidden">
        <div className="text-center mb-8">
          <BrandNameplate brand={brand || { name: displayName }} matchKey={matchKey} />
          {brand?.tagline && (
            <p className="mt-3 text-sand/70 font-light text-base">{brand.tagline}</p>
          )}
          <div className="divider mx-auto mt-5" />
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-6">
          {items.map((p) => (
            <BrandProductCard key={p.id} product={p} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Compact horizontal product card used in the orbit around the brand logo.
function BrandOrbitCard({ product, align = "left" }) {
  return (
    <Link
      to={`/shop/${product.slug}`}
      data-testid={`brand-orbit-product-${product.slug}`}
      className={`group float-card bg-white border border-tan/40 overflow-hidden hover:border-copper transition-all flex items-center gap-4 p-3 ${
        align === "right" ? "flex-row-reverse text-right" : "text-left"
      }`}
    >
      <div className="relative w-20 h-24 shrink-0 overflow-hidden bg-white">
        {product.image_url && (
          <img
            src={mediaUrl(product.image_url)}
            alt={product.name}
            className="w-full h-full object-contain group-hover:scale-[1.05] transition-transform duration-500"
          />
        )}
        {product.is_new && (
          <span className="absolute top-1 left-1 bg-copper text-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em]">New</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-serif text-lg text-navy leading-tight truncate">{product.name}</div>
        <div className="mt-1 text-sm text-copper">{priceLabel(product)}</div>
        <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-charcoal/60 group-hover:text-navy">View →</div>
      </div>
    </Link>
  );
}
